// ============================================
// FILE: src/components/AboutUsScreen.jsx
// ============================================

import { ArrowLeft, CheckCircle, Phone, Mail, MapPin } from 'lucide-react';

const AboutUsScreen = ({ onBack = null, appSettings, setActiveTab, appTheme = 'blue' }) => {
  const appName = appSettings?.appName        || 'Milktone';
  const contact = appSettings?.supportContact || '+91 88266-00232';
  const email   = appSettings?.supportEmail   || 'support@dealstore.app';
  const address = appSettings?.corportateAddress
    || 'ALGOLIVE (OPC) PRIVATE LIMITED, OFFICE 719, 7th floor, SVH, 83 Metrostreet, Sector 83, Gurugram, Haryana 122004';
  const version = appSettings?.version || '1.0.0';
  const logoSrc = `${process.env.PUBLIC_URL || ''}/logo.png`;

  const goHome = () => setActiveTab && setActiveTab('home');
  const openSupportPage = () => {
    if (typeof onBack === 'function') {
      onBack();
    }
    if (typeof setActiveTab === 'function') {
      setActiveTab('help');
    }
  };

  const milestones = [
    { icon: '👥', stat: '10K+',    label: 'Customers'  },
    { icon: '🚚', stat: '1 Lakh+', label: 'Deliveries' },
    { icon: '⭐', stat: '4.8',     label: 'Rating'     },
    { icon: '🌾', stat: '20+',     label: 'Farms'      },
    { icon: '🥛', stat: '50+',     label: 'Products'   },
    { icon: '🏆', stat: '3+',      label: 'Years'      },
  ];

  const values = [
    { icon: '🌿', title: 'Farm Fresh',      desc: 'Sourced directly from trusted local farms.',    bg: 'bg-green-50'  },
    { icon: '⏰', title: 'On-Time',         desc: 'Morning, afternoon or evening — your choice.',  bg: 'bg-blue-50'   },
    { icon: '🛡️', title: 'Quality Assured', desc: 'Strict quality checks before every delivery.',  bg: 'bg-purple-50' },
    { icon: '❤️', title: 'Customer First',  desc: 'Your satisfaction is everything to us.',        bg: 'bg-red-50'    },
  ];

  // Header style for theme consistency with CartScreen
  const headerStyle = appTheme === 'white'
    ? { background: '#fff', color: '#222', boxShadow: '0 2px 16px 0 rgba(0,0,0,0.04)' }
    : { background: 'var(--primary-gradient)', color: 'var(--text-light)' };

  return (
    <div className="pb-20 min-h-screen" style={{ background: 'var(--primary-bg)', color: 'var(--text-main)' }}>
      {/* ─── HEADER ─── */}
      <div className="p-6 rounded-b-3xl" style={headerStyle}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack || goHome}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-all"
            >
              <ArrowLeft size={22} />
            </button>
            <h2 className="text-lg font-semibold" style={appTheme === 'white' ? { color: '#222' } : { color: 'var(--text-light)' }}>About Us</h2>
          </div>
        </div>
        <div className="flex items-center gap-3 min-w-0 pr-3">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center shadow-sm flex-shrink-0" style={appTheme === 'white' ? { background: '#f3f4f6', opacity: 0.9 } : { background: 'var(--primary-bg-light)', opacity: 0.9 }}>
            <img
              src={logoSrc}
              alt={appName}
              className="w-8 h-8 rounded-xl object-contain"
            />
          </div>
          <div className="min-w-0">
            <h1 className="app-brand-title truncate" style={appTheme === 'white' ? { color: '#222' } : { color: 'var(--text-light)' }}>{appName}</h1>
            <p className="text-xs font-medium truncate" style={appTheme === 'white' ? { color: '#fef3c7' } : { color: 'var(--accent-light)' }}>Fresh picks & daily savings</p>
          </div>
        </div>
      </div>

      {/* ─── CONTENT ─── */}
      <div className="p-4 space-y-4">

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3">
          {milestones.map((m, i) => (
            <div key={i} className="bg-white rounded-xl p-3 text-center shadow-sm">
              <span className="text-2xl">{m.icon}</span>
              <p className="text-base font-bold text-gray-800 mt-1">{m.stat}</p>
              <p className="text-xs text-gray-500">{m.label}</p>
            </div>
          ))}
        </div>

        {/* Our Mission */}
        <div className="bg-white rounded-xl shadow-sm p-4">
          <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <span className="text-lg">🎯</span> Our Mission
          </h3>
          <p className="text-gray-600 text-sm leading-relaxed">
            At <span className="font-semibold text-gray-800">{appName}</span>, we believe every family
            deserves access to fresh, pure, and nutritious dairy products — delivered right to their door
            without hassle. We connect local farms with urban households, cutting out the middleman so you
            get the freshest products at the best prices.
          </p>
        </div>

        {/* Our Story */}
        <div className="bg-white rounded-xl shadow-sm p-4">
          <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <span className="text-lg">📖</span> Our Story
          </h3>
          <p className="text-gray-600 text-sm leading-relaxed mb-2">
            <span className="font-semibold text-gray-800">{appName}</span> was born from a simple idea —
            milk should be as fresh as it gets, delivered before you wake up. What started as a small
            neighbourhood service has grown into a platform trusted by thousands of families across the region.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed">
            We partner with certified local dairy farms, maintain a cold-chain from farm to doorstep,
            and use technology to make the entire process seamless for you.
          </p>
        </div>

        {/* What We Offer */}
        <div className="bg-white rounded-xl shadow-sm p-4">
          <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <span className="text-lg">🛒</span> What We Offer
          </h3>
          {[
            { text: 'Fresh cow & buffalo milk',          emoji: '🥛' },
            { text: 'Curd, paneer, ghee & butter',       emoji: '🧈' },
            { text: 'Flexible daily subscriptions',      emoji: '🔔' },
            { text: 'Same-day and morning delivery',     emoji: '🚚' },
            { text: 'Easy pause, skip & cancel options', emoji: '⚡' },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-3 py-2 border-b border-gray-50 last:border-0">
              <CheckCircle size={16} className="text-green-500 flex-shrink-0" />
              <span className="text-sm text-gray-700">{item.emoji} {item.text}</span>
            </div>
          ))}
        </div>

        {/* What We Stand For */}
        <div className="bg-white rounded-xl shadow-sm p-4">
          <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <span className="text-lg">💡</span> What We Stand For
          </h3>
          <div className="grid grid-cols-2 gap-3">
            {values.map((v, i) => (
              <div key={i} className={`${v.bg} rounded-xl p-3 text-center`}>
                <span className="text-2xl">{v.icon}</span>
                <p className="font-semibold text-gray-800 text-sm mt-1">{v.title}</p>
                <p className="text-gray-500 text-xs mt-0.5 leading-snug">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Contact */}
        <div className="bg-white rounded-xl shadow-sm p-4">
          <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <span className="text-lg">📞</span> Get in Touch
          </h3>
          <div className="space-y-3">
            {contact && (
              <button
                type="button"
                onClick={openSupportPage}
                className="w-full flex items-center gap-3 p-3 bg-gray-50 rounded-xl hover:bg-blue-50 transition-colors"
              >
                <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Phone size={16} className="text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-400">Call Us</p>
                  <p className="text-sm font-semibold text-gray-800">{contact}</p>
                </div>
              </button>
            )}
            {email && (
              <button
                type="button"
                onClick={openSupportPage}
                className="w-full flex items-center gap-3 p-3 bg-gray-50 rounded-xl hover:bg-red-50 transition-colors"
              >
                <div className="w-9 h-9 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Mail size={16} className="text-red-500" />
                </div>
                <div>
                  <p className="text-xs text-gray-400">Email Us</p>
                  <p className="text-sm font-semibold text-gray-800 break-all">{email}</p>
                </div>
              </button>
            )}
            <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
              <div className="w-9 h-9 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                <MapPin size={16} className="text-gray-500" />
              </div>
              <div>
                <p className="text-xs text-gray-400">Corporate Office</p>
                <p className="text-sm font-semibold text-gray-800 leading-snug mt-0.5">{address}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-gray-400 py-2">
          v{version} &nbsp;·&nbsp; © {new Date().getFullYear()} {appName}. All rights reserved.
        </p>

      </div>
    </div>
  );
};

export default AboutUsScreen;